import type { FeedPage, ShortVideo } from '~/composables/shorts';
import type { Property } from '~/core/entities/property';
import type { MaybeRefOrGetter } from 'vue';

import {
	mapPropertyToFeedItem,
	toShortVideoProperty,
	uniqueFeedItemsByCommercialId
} from '~/composables/shorts';
import { extractErrorLogContext, useLogger } from '~/composables/useLogger';
import { hasShortsVideo, useStaleGuard } from '~/utils';
import { nuxtPropertiesRepository } from '~~/adapters/repositories/nuxt';

import { buildAgentExclusivePropertiesFilters } from './agentExclusivePropertiesFilters';

/** Tamanho do recorte carregado pelo carrossel do perfil. */
export const AGENT_EXCLUSIVE_PROPERTIES_PER_PAGE = 12;

/**
 * Dono das captações exclusivas de um corretor. Faz uma request independente
 * para a rota compartilhada de imóveis, filtrada por `agentSlug` +
 * `isExclusive`, e guarda tanto o recorte exibido no carrossel quanto o total
 * da busca, que alimenta o "Ver todos".
 */
export function useAgentExclusiveProperties(slug: MaybeRefOrGetter<string>) {
	const logger = useLogger('useAgentExclusiveProperties');
	const guard = useStaleGuard();
	const { user } = useUserSession();

	const properties = shallowRef<Property[]>([]);
	const total = ref(0);
	const isLoading = ref(false);
	const isLoaded = ref(false);

	const hasProperties = computed<boolean>(() => properties.value.length > 0);

	/**
	 * Só imóveis com vídeo publicado entram na playlist: sem vídeo o feed
	 * abriria numa galeria de fotos, que não é o que "Explorar em vídeo"
	 * promete.
	 */
	const videoItems = computed<ShortVideo[]>(() =>
		uniqueFeedItemsByCommercialId(
			properties.value
				.filter(property => hasShortsVideo(property))
				.map(property => mapPropertyToFeedItem(toShortVideoProperty(property)))
				.filter((item): item is ShortVideo => item !== null)
		)
	);

	function reset(): void {
		properties.value = [];
		total.value = 0;
		isLoaded.value = true;
	}

	async function load(): Promise<void> {
		const currentSlug = toValue(slug);

		if (!currentSlug) {
			reset();
			return;
		}

		const capturedId = guard.next();
		isLoading.value = true;

		try {
			const [result, error] = await nuxtPropertiesRepository.list({
				...buildAgentExclusivePropertiesFilters(currentSlug),
				page: 1,
				perPage: AGENT_EXCLUSIVE_PROPERTIES_PER_PAGE,
				markUserWishlistsId: user.value?.id
			});

			if (guard.isStale(capturedId)) return;

			if (error || !result) {
				logger.error('Failed to fetch agent exclusive properties', {
					...(error
						? extractErrorLogContext(error)
						: {
								error_message:
									'No agent exclusive properties result returned from repository'
							}),
					agent_slug: currentSlug
				});
				reset();
				return;
			}

			properties.value = result.properties ?? [];
			total.value = result.pagination?.filteredCount ?? properties.value.length;
			isLoaded.value = true;
		} finally {
			if (!guard.isStale(capturedId)) isLoading.value = false;
		}
	}

	/**
	 * Playlist entregue ao feed de shorts: as próprias captações exclusivas do
	 * corretor, na ordem do carrossel, começando no imóvel clicado. O cursor não
	 * pagina e não carrega filtro, então ao fim da lista o reel cai na tela de
	 * expansão genérica em vez de virar um feed qualquer.
	 */
	function snapshot(commercialId: string): FeedPage {
		const startIndex = videoItems.value.findIndex(
			item => item.property.commercialId === commercialId
		);
		const items =
			startIndex >= 0 ? videoItems.value.slice(startIndex) : videoItems.value;

		return {
			items,
			cursor: {
				filters: {},
				page: 1,
				perPage: AGENT_EXCLUSIVE_PROPERTIES_PER_PAGE,
				hasMore: false
			},
			meta: {
				totalAvailable: items.length,
				/**
				 * O feed segura o `shorts_viewed_entry` até conhecer o total de
				 * vídeos da recomendação. Como esta playlist não pagina, nenhuma
				 * página seguinte preencheria esse contador depois — sem os counts
				 * o evento nunca sairia e a sessão iniciada no perfil ficaria
				 * invisível nas métricas. A lista só tem vídeos, por construção.
				 */
				counts: { totalWithVideos: items.length, totalWithoutVideos: 0 }
			}
		};
	}

	return {
		properties,
		total,
		isLoading,
		isLoaded,
		hasProperties,
		videoItems,
		load,
		snapshot
	};
}
